import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import { CSSProperties, ReactNode } from "react";
import styled from "styled-components";
import Text from "common/Typography/Text";
import { useBottomNavigator } from "utils/hooks/misc.tsx";

export interface PairButtonsProps {
  pairOptions: {
    pair: string;
    isSelected: boolean;
    onClick: () => void;
    numSymbols: number;
  }[];
  bordered?: boolean;
  style?: CSSProperties;
  hideTitle?: boolean;
  extraTag?: ReactNode;
}

const PairButtons = ({
  pairOptions,
  bordered,
  style,
  hideTitle,
  extraTag,
}: PairButtonsProps) => {
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  return (
    <Wrapper style={style} $bordered={bordered}>
      {!hideTitle && !isBottomNavigator && (
        <Text type="secondary" style={{ marginBottom: 8 }}>
          Pairs
        </Text>
      )}
      <PairsWrapper $isBottomNavigator={isBottomNavigator} $bordered={bordered}>
        {extraTag}
        {pairOptions.map((pairData) => {
          return (
            <PairButton
              onClick={pairData.onClick}
              key={pairData.pair}
              $selected={pairData.isSelected}
            >
              {pairData.pair}
              <Text $size="S" style={{ color: "inherit", marginLeft: 4 }}>
                ({pairData.numSymbols})
              </Text>
            </PairButton>
          );
        })}
      </PairsWrapper>
    </Wrapper>
  );
};

export default PairButtons;

const Wrapper = styled.div<{ $bordered?: boolean }>`
  ${(p) =>
    p.$bordered &&
    `
    margin: 0 14px;
    padding: 8px 0 12px 0;
    border-bottom: ${p.theme.borders.light};
  `};
`;
const PairsWrapper = styled.div<{
  $isBottomNavigator?: boolean;
  $bordered?: boolean;
}>`
  display: flex;
  column-gap: 8px;
  row-gap: 8px;
  flex-wrap: wrap;
  ${(p) =>
    p.$isBottomNavigator &&
    p.$bordered &&
    `
    padding-top: 12px;
    border-top: ${p.theme.borders.light};
  `};
`;
export const PairButton = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 20px;
  cursor: pointer;
  user-select: none;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  background-color: ${(p) => p.theme.colors.filterChipsColor};
  color: ${(p) => p.theme.colors.primaryText};
  ${(p) =>
    p.$selected &&
    `
    background-color: ${p.theme.colors.primaryBlue};
    color: ${p.theme.colors.buttonText};
  `};
  ${(p) =>
    p.theme.isNewLook &&
    `
    min-height: 28px;
    padding: 0 12px;
    border-radius: ${NEW_LOOK_RADIUS.chip}px;
    font-size: 12.5px;
    background-color: ${p.theme.colors.cardLightBgColor};
    color: ${p.theme.colors.secondaryText};
    box-shadow: inset 0 0 0 1px ${p.theme.colors.cardBorderColor};
    transition: color 0.2s, background-color 0.2s, box-shadow 0.2s;
    &:hover {
      color: ${p.theme.colors.titleColor};
      box-shadow: inset 0 0 0 1px ${p.theme.colors.lightBorderGrey};
    }
    ${
      p.$selected
        ? `
      background-color: ${p.theme.colors.lightBlue};
      color: ${p.theme.colors.primaryBlue};
      font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      box-shadow: inset 0 0 0 1px ${p.theme.colors.midBlue};
      &:hover {
        color: ${p.theme.colors.primaryBlue};
        box-shadow: inset 0 0 0 1px ${p.theme.colors.midBlue};
      }
    `
        : ""
    }
  `};
`;
